import Ajv from 'ajv'
import ArcUUID from '~/util/arc-identifiers'
import * as R from 'ramda'
import angular from 'angular'

export default [
  '$rootScope',
  '$timeout',
  'uiStateManagerHistoryService',
  uiStateManagerService,
]
const DEBUG = false

/*
 * With a multitude of states being broadcasted, it would be nice to have
 *  a framework we can untilize to manage the global display of the
 *  user interface. Angular likes that that we have to
 *  use an $emit or $broadcast in order to manage scope state. However,
 *  there is a better way. In game interface design, you have to manage
 *  user state while running a game loop in the background. You can't
 *  combine the two loop-states because the game loop could fall to 10fps
 *  and you still want the user interface to be responsive.
 *
 *  The first approach is to use an event bus, IE $rootScope. The second
 *  is a web-socket pub/sub. The best IMO is to use three isolated
 *  concepts consisting of two loops and one data-state.
 *
 * The first loop is encapsolated by an angularDirective and two way binding.
 *  Currently, to hook into a composet style code without refactoring the
 *  entire codebase, we'll have to create a shim. uiStateManager/directive.js
 *  The directive will manage what is viewed and what isn't inside the
 *  embuded HTML templates.
 *
 * The second loop is encapsolated by an angularService and listens to
 *  $rootScope for updates then makes decisions depending on the intent.
 *  The decisions can be expanded into a single file, here, as the code
 *  is refactored.
 *
 * The data-state is what is sent to the directives from the service.
 *  Ideally an immutable data-structure, but well manage with
 *  object-literals for now.
 *
 *  Kind of like pub/sub, but with a twist. The data will be pushed to
 *  the directives from $timeout rather than from the $rootScope.$on or
 *  respective trigger events. This will allow us to assign a priority
 *  and polling interface. I'll skip the priority codes for now.
 *
 *  Why? Because this will defend against the rapid clicker updatingthe
 *  user interface two billion times a second. REDUX THIS!
 *
 */
function uiStateManagerService(
  $rootScope,
  $timeout,
  uiStateManagerHistoryService
) {
  const stateHandlers = {}

  /*
   * To maintain a constent and reliable speed, we'll have to type-check
   * the json data-structures coming into the pipeline. There are four required
   * properties.
   *  - state: active/disabled
   *  - stateName: <str>
   *  - payload: <object>
   *  - priority: <int>
   *
   * In a user interface componet, its safer to assume that a number of objects
   *  with the same stateName can be squashed together. Combining the payload into
   *  a single object will enrich the eventual outcome of the state transition.
   *
   * Power users like to rapid click componets, but sometimes our application
   *  won't be able to keep up. Priority allows us to cancel state-requests with
   *  the same stateName in favor of state-requests with a high priority.
   *
   *  State-requests with the same stateName and priority should be combined into
   *  a single state-request and flushed.
   *
   * Because we're buffering the state-requests, we can reduce and replay the requests.
   *  We can even log the requests and replay user-actions remotely.
   *  ( Something we can shim in later. )
   */
  const bufferValidator = new Ajv().compile({
    title: 'uiStateManagerServiceBufferSchema',
    type: 'object',
    properties: {
      state: {
        type: 'string',
        enum: ['disabled', 'active'],
      },
      stateName: { type: 'string' },
      payload: { type: 'object' },
      priority: { type: 'integer' },
    },
    required: ['state', 'stateName', 'payload', 'priority'],
  })

  /*
   * Flush will decide how much of the buffer we're interested in, keeping us from
   *  having to lock state at any point in time.
   *
   * // Read/Write nature, LIFO
   */
  const bufferState = []
  const buffer = function uiStateManagerServiceBuffer(stateRequest) {
    /*
     * The UI is in a single thread, therefor we can manage state of the validator.
     *  I hate this pattern, but I'll take advantage of it for performance reasons.
     *
     * It also looks like Ajv manages its own state...so we might be able to
     *  assume the the datatypes we target will always line up.
     */
    if (bufferValidator(stateRequest)) {
      bufferState.push(stateRequest)
      return true
    } else {
      clearFlusher()
      console.error('Unable to understand stateRequest')
      let idx = 0
      for (idx; idx < bufferValidator.errors.length; idx++) {
        let error = bufferValidator.errors[idx]
        console.error(error)
      }
      return false
    }
  }
  // UIEvent Buffer
  // Used to suspend data-flow until the user interfaces has loaded.
  let bufferSize = 100
  const flush = function uiStateManagerServiceFlush() {
    // http://ramdajs.com/docs/#filter
    // Clone the objects and empty the buffer so that we don't have any unwanted mutations...
    let bufferIdx =
      bufferState.length > bufferSize ? bufferSize : bufferState.length
    let bufferedStateRequests = []
    // Reverse loop
    for (bufferIdx; bufferIdx > 0 && bufferState.length > 0; --bufferIdx) {
      bufferedStateRequests.push(bufferState.pop())
    }

    let stateTriggers = {}
    let idx = 0
    for (idx; idx < bufferedStateRequests.length; idx++) {
      let trigger = bufferedStateRequests[idx]
      if (
        !Object.prototype.hasOwnProperty.call(
          stateTriggers,
          trigger['stateName']
        )
      ) {
        stateTriggers[trigger['stateName']] = [trigger]
        continue
      }
      let jdx = 0
      let found = false
      for (jdx; jdx < stateTriggers[trigger['stateName']].length; jdx++) {
        let extTrigger = stateTriggers[trigger['stateName']][jdx]
        // Intentionally ignoring handling active/disabled because FIFO + merging payloads.
        //  Anything registered with the same stateHandler should be able to handle all
        //  payloads that go to it, reguardless of state-type.
        if (extTrigger['priority'] === trigger['priority']) {
          extTrigger['payload'] = R.mergeAll(
            extTrigger['payload'],
            trigger['payload']
          )
          found = true
          break
        }
      }
      if (!found) {
        stateTriggers[trigger['stateName']].push(trigger)
      }
    }
    for (let [triggerStateName, triggerPayloads] of Object.entries(
      stateTriggers
    )) {
      triggerPayloads.sort(function(alpha, bravo) {
        if (alpha['priority'] > bravo['priority']) {
          return 1
        } else if (alpha['priority'] < bravo['priority']) {
          return -1
        }
        return 0
      })
      let tdx = 0
      for (tdx; tdx < triggerPayloads.length; tdx++) {
        if (
          !Object.prototype.hasOwnProperty.call(stateHandlers, triggerStateName)
        ) {
          if (DEBUG) {
            console.error('missing state trigger handler', triggerStateName)
          }
          continue
        }
        // Execute!
        let sdx = 0
        for (sdx; sdx < stateHandlers[triggerStateName].length; sdx++) {
          stateHandlers[triggerStateName][sdx]['callback'](triggerPayloads[tdx])
          uiStateManagerHistoryService.store(
            triggerStateName,
            triggerPayloads[tdx]
          )
        }
      }
    }
    return undefined
  }
  /*
   * Delay up to 1000ms for testing, but make sure to reset to something below 50ms.
   *  The average user can experiance a response within 150ms, 250ms for the slow,
   *  and 50ms for a fluent power user.
   *
   *  I like 10ms. A well built handler shouldn't take more than 10-20ms to execute.
   */
  // const delayFlush = 20
  const delayFlush = 100
  // const delayFlush = 1000
  /*
   * flusher will retain the reference to the $timeout() result so we may
   *  interupt it.
   */
  var flusher = null
  const clearFlusher = function uiStateManagerServiceFlushClearer() {
    if (flusher === null || angular.isUndefined(flusher)) {
      return undefined
    }
    $timeout.cancel(flusher)
    flusher = null
  }
  const startFlusher = function uiStateManagerServiceFlushStater() {
    if (flusher !== null && angular.isDefined(flusher)) {
      return undefined
    }

    flusher = $timeout(delayFlushHandler, delayFlush)
  }
  /*
   * The recursive callback should only be triggered at the end of the handler.
   *  otherwise we risk having two or three handlers running at the same time
   *  because of slowly executing logic.
   * It is the developers responsibility to make sure the function executes as
   *  quickly as possible.
   */
  const delayFlushHandler = function uiStateManagerServiceFlush() {
    clearFlusher()
    flush()
    startFlusher()
    return undefined
  }
  // End

  /*
   * Sender and action are angular's $scope.$on('...', function(sender, action) {}) respectivly.
   */
  const stateChangeValidator = new Ajv().compile({
    title: 'triggerStateChangeSchema',
    type: 'object',
    properties: {
      sender: { type: 'object' },
      action: { type: ['object', 'string', 'integer'] },
    },
    required: ['sender', 'action'],
  })
  this.triggerStateChange = function triggerStateChangeHandler(
    stateName,
    payload
  ) {
    if (stateChangeValidator(payload)) {
      buffer(payload)
    } else {
      clearFlusher()
      let idx = 0
      for (idx; idx < stateChangeValidator.length; idx++) {
        let error = stateChangeValidator.errors[idx]
        console.error(error)
      }
    }
  }
  /*
   * onStateChange.priority is not to be confused with buffer.priority.
   *  onStateChange.priority is more about which callback will be called first
   *  arbirtaty to the order it was registered. Allowing something to register later
   *  in the code initalization, but called first because it maybe more imparative to
   *  render first rather than last.
   *
   * For example, if you need to update the UI before making a data request, you'll want to
   *  have all UIElements registered with a lower priority, because lower priorities are
   *  executed first.
   */
  const sortStateHandlers = function sortStateHandlers(alpha, bravo) {
    if (alpha.priority > bravo.priority) {
      return -1
    }
    if (alpha.priority < bravo.priority) {
      return 1
    }
    return 0
  }
  this.removeOnStateChange = function removeOnStateChange(stateName, identity) {
    let length = stateHandlers[stateName].length
    stateHandlers[stateName] = stateHandlers[stateName].filter(
      function removeOnStateChangeHandler(payload) {
        return payload.identity !== identity
      }
    )
    stateHandlers[stateName].sort(sortStateHandlers)
    // Removed or not?
    return length !== stateHandlers[stateName].length
  }
  this.onStateChange = (stateName, callback, priority = 10) => {
    if (!Object.prototype.hasOwnProperty.call(stateHandlers, stateName)) {
      stateHandlers[stateName] = []
    }
    let identity = ArcUUID.create()
    stateHandlers[stateName].push({
      callback: callback,
      priority: priority,
      identity: identity,
    })
    stateHandlers[stateName].sort(sortStateHandlers)
    return function() {
      return this.removeOnStateChange(stateName, identity)
    }.bind(this)
  }
  this.reset = function reset() {
    clearFlusher()
    flush()
    startFlusher()
  }
  this.reset()
}
